<template>
  <div>
    <!-- Header con botón volver -->
    <div class="d-flex justify-content-between align-items-center pt-50 mb-2">
      <h2 class="font-weight-bolder m-0">
        <span @click="$router.go(-1)">
          <b-button
            variant="white"
            class="btn-icon rounded-circle mr-50 text-primary bg-white"
            size="sm"
          >
            <feather-icon icon="ChevronLeftIcon" />
          </b-button>
        </span>
        <span v-if="calendar">{{ calendar.name }}</span>
        <span v-else>Festivos del calendario</span>
      </h2>
      <div class="d-flex align-items-center">
        <b-form-group
          label="Año"
          label-for="year-select"
          class="mb-0 mr-2"
        >
          <b-form-select
            id="year-select"
            v-model="year"
            :options="yearOptions"
            size="sm"
            @change="onYearChange"
          />
        </b-form-group>
        <b-button
          variant="primary"
          @click="showAddModal = true"
        >
          Añadir festivo
        </b-button>
      </div>
    </div>

    <!-- Modal para añadir festivo -->
    <b-modal
      v-model="showAddModal"
      title="Añadir festivo"
      size="md"
      centered
      hide-footer
      @hidden="showAddModal = false"
    >
      <add-holiday
        :calendar-id="calendarId"
        @success="handleAddSuccess"
        @cancel="showAddModal = false"
      />
    </b-modal>

    <!-- Info del calendario -->
    <b-card
      v-if="calendar"
      class="mb-2"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="MapPinIcon"
            size="18"
            class="text-muted mr-1"
          />
          <span class="text-muted">
            <span v-if="calendar.country">{{ calendar.country }}</span>
            <span v-if="calendar.autonomous_community"> - {{ calendar.autonomous_community }}</span>
          </span>
        </div>
        <div
          v-if="calendar.sync_public_holidays"
          class="d-flex align-items-center"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="16"
            class="text-success mr-50"
          />
          <span class="text-success small">
            Sincronización automática activa
          </span>
        </div>
      </div>
    </b-card>

    <!-- Contenido principal -->
    <div v-if="calendar && calendar.holidays">
      <template v-if="calendar.holidays && calendar.holidays.length > 0">
        <div class="holidays-grid">
          <b-card
              v-for="holiday in sortedHolidays"
              :key="holiday.id"
              class="holiday-card mb-2"
              :class="getHolidayCardClass(holiday)"
            >
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-1">
                    <feather-icon
                      icon="CalendarIcon"
                      :class="getHolidayIconClass(holiday)"
                      size="20"
                      class="mr-75"
                    />
                    <h5 class="mb-0 font-weight-bold">
                      {{ holiday.name }}
                    </h5>
                  </div>

                  <div class="d-flex align-items-center text-muted mb-1">
                    <feather-icon
                      icon="ClockIcon"
                      size="16"
                      class="mr-50"
                    />
                    <span class="font-weight-bold">{{ formatDate(holiday.date) }}</span>
                  </div>

                  <div class="d-flex align-items-center flex-wrap">
                    <span
                      v-if="holiday.type === 'national'"
                      class="custom-badge badge-holiday mr-75 mb-50"
                    >
                      <feather-icon
                        icon="FlagIcon"
                        size="14"
                        class="mr-25"
                      />
                      Nacional
                    </span>

                    <span
                      v-if="holiday.type === 'autonomous'"
                      class="custom-badge badge-today mr-75 mb-50"
                    >
                      <feather-icon
                        icon="MapPinIcon"
                        size="14"
                        class="mr-25"
                      />
                      Autonómico
                    </span>

                    <span
                      v-if="holiday.is_recurring"
                      class="custom-badge badge-approved mb-50"
                    >
                      <feather-icon
                        icon="RefreshCwIcon"
                        size="14"
                        class="mr-25"
                      />
                      Recurrente
                    </span>
                  </div>
                </div>

                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="btn-icon ml-1"
                  @click="deleteHoliday(holiday.id, holiday.name)"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="18"
                  />
                </b-button>
              </div>
            </b-card>
          </div>

          <b-card class="mt-2">
            <div class="d-flex justify-content-between align-items-center">
              <div class="text-muted">
                <feather-icon
                  icon="CalendarIcon"
                  size="16"
                  class="mr-50"
                />
                <span class="font-weight-bold">Total: {{ calendar.holidays.length }} festivos</span>
              </div>
              <div class="d-flex align-items-center">
                <span class="custom-badge badge-holiday mr-75">
                  <feather-icon
                    icon="FlagIcon"
                    size="14"
                    class="mr-25"
                  />
                  {{ nationalCount }} Nacionales
                </span>
                <span class="custom-badge badge-today">
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="mr-25"
                  />
                  {{ autonomousCount }} Autonómicos
                </span>
              </div>
            </div>
          </b-card>
        </template>

        <template v-else>
          <b-alert
            show
            variant="info"
          >
            <div class="alert-body">
              <feather-icon
                icon="InfoIcon"
                class="mr-50"
              />
              No hay festivos configurados para este calendario en el año {{ year }}.
            </div>
          </b-alert>
        </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  BCard,
  BButton,
  BAlert,
  BFormGroup,
  BFormSelect,
  BModal,
} from 'bootstrap-vue'
import AddHoliday from './AddHoliday.vue'

export default {
  components: {
    BCard,
    BButton,
    BAlert,
    BFormGroup,
    BFormSelect,
    BModal,
    AddHoliday,
  },
  data() {
    return {
      year: new Date().getFullYear(),
      showAddModal: false,
      calendarId: null,
    }
  },
  computed: {
    ...mapGetters({
      calendar: 'calendar/getCalendar',
    }),
    sortedHolidays() {
      if (!this.calendar || !this.calendar.holidays) return []
      return [...this.calendar.holidays].sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA - dateB
      })
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      return [
        { value: currentYear, text: currentYear.toString() },
        { value: currentYear + 1, text: (currentYear + 1).toString() },
      ]
    },
    nationalCount() {
      if (!this.calendar || !this.calendar.holidays) return 0
      return this.calendar.holidays.filter(h => h.type === 'national').length
    },
    autonomousCount() {
      if (!this.calendar || !this.calendar.holidays) return 0
      return this.calendar.holidays.filter(h => h.type === 'autonomous').length
    },
  },
  async mounted() {
    this.calendarId = parseInt(this.$route.params.id, 10)
    await this.getCalendarData({ id: this.calendarId, year: this.year })
  },
  methods: {
    ...mapActions({
      getCalendarData: 'calendar/getCalendar',
      deleteHolidayAction: 'calendar/deleteHoliday',
    }),
    async onYearChange() {
      await this.getCalendarData({ id: this.calendarId, year: this.year })
    },
    handleAddSuccess() {
      this.showAddModal = false
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      const options = { day: '2-digit', month: 'long', year: 'numeric' }
      return date.toLocaleDateString('es-ES', options)
    },
    getHolidayCardClass(holiday) {
      if (holiday.type === 'national') {
        return 'border-left-primary'
      }
      if (holiday.type === 'autonomous') {
        return 'border-left-info'
      }
      return 'border-left-secondary'
    },
    getHolidayIconClass(holiday) {
      if (holiday.type === 'national' || holiday.type === 'autonomous') {
        return 'text-warning'
      }
      return 'text-secondary'
    },
    deleteHoliday(id, name) {
      this.$bvModal
        .msgBoxConfirm(`¿Estás seguro de eliminar ${name}?`, {
          bodyClass: 'bodyDelete',
          footerClass: 'footerDelete',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          cancelVariant: 'outline-danger',
          centered: true,
        })
        .then(value => {
          if (value === true) {
            this.deleteHolidayAction({ id_calendar: this.calendarId, id_holiday: id })
          }
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.holidays-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}

.holiday-card {
  border-left-width: 4px !important;
  border-left-style: solid !important;
  transition: all 0.3s ease;
  cursor: default;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.border-left-primary {
    border-left-color: #ffc078 !important;
  }

  &.border-left-info {
    border-left-color: rgba(156, 206, 217, 0.8) !important;
  }

  &.border-left-secondary {
    border-left-color: #82868b !important;
  }
}
</style>
